import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, CheckCircle, Code, Clock, Receipt, Palette, ExternalLink, Trash2, AlertTriangle, Package, Wallet, ArrowDownCircle, CreditCard, Ban, Zap, Mail, Calendar, Send, AlertCircle, Bell, BellRing, MessageSquare, ShoppingCart, CreditCard as PaymentIcon, XCircle, Users, Timer, Smartphone, UserPlus, Shield, Eye, Settings2, FileText, Plus, GitBranch } from "lucide-react";
import { EmailPreviewDialog } from "@/components/EmailPreviewDialog";

import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatCurrency, getCurrencySymbol } from "@/lib/currency";
import { fetchSupabaseFunction, invokeShopifyEmbeddedFunction } from "@/lib/shopifySessionToken";
import { format, formatDistanceToNow } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, RefreshCw } from "lucide-react";

interface MerchantPreferencesProps {
  merchantId: string;
  storeDomain: string;
  canEdit: boolean;
  currentPlan?: string;
  onNavigateToBilling?: () => void;
  merchantEmail?: string | null;
  isSuperAdmin?: boolean;
  platformType?: string;
}

type CartRuleOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'one_of'
  | 'has_value'
  | 'is_empty';

type CartRuleConditionSource = 'cart_attribute' | 'selector';
type SelectorReadMode = 'attribute' | 'value' | 'text' | 'exists' | 'visible' | 'checked' | 'class';

interface CartAttributeRuleCondition {
  source: CartRuleConditionSource;
  attribute: string;
  selector: string;
  selectorRead: SelectorReadMode;
  selectorReadKey: string;
  operator: CartRuleOperator;
  value: string;
}

interface CartAttributeRule {
  id: string;
  name: string;
  enabled: boolean;
  match: 'all' | 'any';
  conditions: CartAttributeRuleCondition[];
  requiredAttributes: string[];
  message: string;
}

interface PreferenceData {
  store_name: string | null;
  email_store_name: string | null;
  default_deadline_hours: number | null;
  default_tax_rate: number | null;
  minimum_split_order_value: number | null;
  customer_pan_collection_enabled: boolean | null;
  customer_pan_required: boolean | null;
  split_discount_enabled: boolean | null;
  split_discount_percentage: number | null;
  split_discount_cap: number | null;
  widget_installed: boolean | null;
  widget_position: string | null;
  brand_color: string | null;
  split_button_color: string | null;
  split_button_text: string | null;
  split_card_color: string | null;
  storefront_help_display: 'none' | 'link' | 'bubble' | null;
  storefront_show_product_button: boolean | null;
  storefront_show_cart_button: boolean | null;
  storefront_show_mini_cart_button: boolean | null;
  storefront_required_cart_attributes: string[] | null;
  storefront_cart_attribute_rules: CartAttributeRule[];
  storefront_cart_validation_message: string | null;
  auto_enable_new_products: boolean | null;
  cancellation_fee_type: 'none' | 'fixed' | 'percentage' | null;
  cancellation_fee_value: number | null;
  currency: string;
  webhook_secret: string | null;
}

interface UninstallStats {
  totalRequests: number;
  completedRequests: number;
  pendingRequests: number;
  totalRevenue: number;
  pendingRevenue: number;
}

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, i) => ({
  value: i + 1,
  label: `${i + 1}${getOrdinalSuffix(i + 1)}`,
}));

function getOrdinalSuffix(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

function getContrastTextColor(hexColor: string): string {
  const hex = hexColor.replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) {
    return "#FFFFFF";
  }

  const red = parseInt(hex.slice(0, 2), 16);
  const green = parseInt(hex.slice(2, 4), 16);
  const blue = parseInt(hex.slice(4, 6), 16);
  const brightness = (red * 299 + green * 587 + blue * 114) / 1000;
  return brightness > 160 ? "#111827" : "#FFFFFF";
}

function parseCartAttributeLines(value: string): string[] {
  return Array.from(new Set(
    value
      .split(/\r?\n|,/)
      .map((line) => line.trim())
      .filter(Boolean),
  ));
}

const CART_RULE_OPERATOR_OPTIONS: Array<{ value: CartRuleOperator; label: string; needsValue: boolean }> = [
  { value: 'equals', label: 'Equals', needsValue: true },
  { value: 'not_equals', label: 'Does not equal', needsValue: true },
  { value: 'contains', label: 'Contains', needsValue: true },
  { value: 'one_of', label: 'Is one of', needsValue: true },
  { value: 'has_value', label: 'Has a value', needsValue: false },
  { value: 'is_empty', label: 'Is empty', needsValue: false },
];

const SELECTOR_READ_OPTIONS: Array<{ value: SelectorReadMode; label: string; needsKey: boolean }> = [
  { value: 'attribute', label: 'Element attribute', needsKey: true },
  { value: 'value', label: 'Input value', needsKey: false },
  { value: 'text', label: 'Text content', needsKey: false },
  { value: 'exists', label: 'Element exists', needsKey: false },
  { value: 'visible', label: 'Element is visible', needsKey: false },
  { value: 'checked', label: 'Input is checked', needsKey: false },
  { value: 'class', label: 'Has CSS class', needsKey: true },
];

function createEmptyCartRuleCondition(): CartAttributeRuleCondition {
  return {
    source: 'cart_attribute',
    attribute: '',
    selector: '',
    selectorRead: 'attribute',
    selectorReadKey: '',
    operator: 'equals',
    value: '',
  };
}

function createCartRuleId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `rule-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function createEmptyCartAttributeRule(): CartAttributeRule {
  return {
    id: createCartRuleId(),
    name: 'New conditional rule',
    enabled: true,
    match: 'all',
    conditions: [createEmptyCartRuleCondition()],
    requiredAttributes: [],
    message: '',
  };
}

function normalizeCartAttributeRules(value: unknown): CartAttributeRule[] {
  if (!Array.isArray(value)) return [];

  return value.slice(0, 25).flatMap((candidate, ruleIndex) => {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return [];
    const rule = candidate as Record<string, unknown>;
    const conditions = Array.isArray(rule.conditions)
      ? rule.conditions.slice(0, 10).flatMap((conditionCandidate) => {
          if (!conditionCandidate || typeof conditionCandidate !== 'object' || Array.isArray(conditionCandidate)) {
            return [];
          }
          const condition = conditionCandidate as Record<string, unknown>;
          const source: CartRuleConditionSource = condition.source === 'selector'
            ? 'selector'
            : 'cart_attribute';
          const attribute = typeof condition.attribute === 'string' ? condition.attribute.trim().slice(0, 200) : '';
          const selector = typeof condition.selector === 'string' ? condition.selector.trim().slice(0, 500) : '';
          const selectorRead = SELECTOR_READ_OPTIONS.some((option) => option.value === condition.selectorRead)
            ? condition.selectorRead as SelectorReadMode
            : 'attribute';
          const selectorReadKey = typeof condition.selectorReadKey === 'string'
            ? condition.selectorReadKey.trim().slice(0, 200)
            : '';
          const operator = typeof condition.operator === 'string'
            ? condition.operator as CartRuleOperator
            : 'equals';
          if (!CART_RULE_OPERATOR_OPTIONS.some((option) => option.value === operator)) return [];

          return [{
            source,
            attribute,
            selector,
            selectorRead,
            selectorReadKey,
            operator,
            value: ['has_value', 'is_empty'].includes(operator)
              ? ''
              : typeof condition.value === 'string'
                ? condition.value.trim().slice(0, 500)
                : '',
          }];
        })
      : [];

    return [{
      id: typeof rule.id === 'string' && rule.id.trim() ? rule.id.trim().slice(0, 100) : `rule-${ruleIndex + 1}`,
      name: typeof rule.name === 'string' && rule.name.trim() ? rule.name.trim().slice(0, 200) : `Rule ${ruleIndex + 1}`,
      enabled: rule.enabled !== false,
      match: rule.match === 'any' ? 'any' : 'all',
      conditions: conditions.length ? conditions : [createEmptyCartRuleCondition()],
      requiredAttributes: Array.isArray(rule.requiredAttributes)
        ? Array.from(new Set(
            rule.requiredAttributes
              .filter((attribute): attribute is string => typeof attribute === 'string')
              .map((attribute) => attribute.trim().slice(0, 200))
              .filter(Boolean),
          )).slice(0, 20)
        : [],
      message: typeof rule.message === 'string' ? rule.message.trim().slice(0, 500) : '',
    }];
  });
}

interface ReportPreferences {
  id?: string;
  email_reports_enabled: boolean;
  report_frequency: "weekly" | "monthly";
  report_day: number;
  report_email: string | null;
  last_report_sent_at: string | null;
}

interface NotificationPreferences {
  id?: string;
  notify_new_order: boolean;
  notify_payment_received: boolean;
  notify_order_completed: boolean;
  notify_order_expired: boolean;
  notify_refund_requested: boolean;
  notify_low_participation: boolean;
  digest_mode: 'instant' | 'daily' | 'weekly';
  digest_time: string;
  notification_email: string | null;
  notification_phone: string | null;
  whatsapp_enabled: boolean;
  reminder_enabled: boolean;
  daily_reminder_enabled: boolean;
  one_hour_reminder_enabled: boolean;
  additional_reminder_enabled: boolean;
  additional_reminder_hours: number;
}

const MerchantPreferences = ({ merchantId, storeDomain, canEdit, currentPlan, onNavigateToBilling, merchantEmail, isSuperAdmin = false, platformType }: MerchantPreferencesProps) => {
  const navigate = useNavigate();
  const isWooCommerceMerchant = platformType === "woocommerce";
  
  const storeLabel = "Store";
  const customersLabel = "Customers";
  const ownerLabel = "Store Owner";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [installingWidget, setInstallingWidget] = useState(false);
  const [uninstallingWidget, setUninstallingWidget] = useState(false);
  const [syncingMetafield, setSyncingMetafield] = useState(false);
  const [showUninstallDialog, setShowUninstallDialog] = useState(false);
  const [uninstallStats, setUninstallStats] = useState<UninstallStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [sendingTestReport, setSendingTestReport] = useState(false);
  const [sendingTestReminder, setSendingTestReminder] = useState<'midpoint' | 'final' | null>(null);
  const [savingReportPrefs, setSavingReportPrefs] = useState(false);
  const [savingNotificationPrefs, setSavingNotificationPrefs] = useState(false);
  
  // Team state
  const [teamMembers, setTeamMembers] = useState<Array<{ id: string; user_id: string; role: string; created_at: string }>>([]);
  const [invitations, setInvitations] = useState<Array<{ id: string; email: string; role: string; status: string; created_at: string; expires_at: string }>>([]);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<string>("view_only");
  const [sendingInvite, setSendingInvite] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [revokingInviteId, setRevokingInviteId] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<PreferenceData>({
    store_name: null,
    email_store_name: null,
    default_deadline_hours: 72,
    default_tax_rate: 0,
    minimum_split_order_value: 0,
    customer_pan_collection_enabled: true,
    customer_pan_required: false,
    split_discount_enabled: false,
    split_discount_percentage: null,
    split_discount_cap: null,
    widget_installed: false,
    widget_position: 'image-top-left',
    brand_color: '#8B5CF6',
    split_button_color: '#3F51B5',
    split_button_text: 'Split with Friends',
    split_card_color: '#F9FAFB',
    storefront_help_display: 'none',
    storefront_show_product_button: true,
    storefront_show_cart_button: true,
    storefront_show_mini_cart_button: true,
    storefront_required_cart_attributes: [],
    storefront_cart_attribute_rules: [],
    storefront_cart_validation_message: null,
    auto_enable_new_products: false,
    cancellation_fee_type: 'none',
    cancellation_fee_value: 0,
    currency: 'USD',
    webhook_secret: null,
  });
  const [requiredCartAttributesText, setRequiredCartAttributesText] = useState("");

  const [reportPreferences, setReportPreferences] = useState<ReportPreferences>({
    email_reports_enabled: true,
    report_frequency: "weekly",
    report_day: 1,
    report_email: null,
    last_report_sent_at: null,
  });

  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferences>({
    notify_new_order: true,
    notify_payment_received: true,
    notify_order_completed: true,
    notify_order_expired: true,
    notify_refund_requested: true,
    notify_low_participation: false,
    digest_mode: 'instant',
    digest_time: '09:00',
    notification_email: null,
    notification_phone: null,
    whatsapp_enabled: false,
    reminder_enabled: true,
    daily_reminder_enabled: false,
    one_hour_reminder_enabled: true,
    additional_reminder_enabled: false,
    additional_reminder_hours: 12,
  });

  useEffect(() => {
    fetchPreferences();
    fetchTeamData();
  }, [merchantId]);

  const fetchPreferences = async () => {
    try {
      // Fetch merchant preferences
      const { data, error } = await supabase
        .from('merchants')
        .select('store_name, email_store_name, default_deadline_hours, default_tax_rate, minimum_split_order_value, customer_pan_collection_enabled, customer_pan_required, split_discount_enabled, split_discount_percentage, split_discount_cap, widget_installed, widget_position, brand_color, split_button_color, split_button_text, split_card_color, storefront_help_display, storefront_show_product_button, storefront_show_cart_button, storefront_show_mini_cart_button, storefront_required_cart_attributes, storefront_cart_attribute_rules, storefront_cart_validation_message, auto_enable_new_products, cancellation_fee_type, cancellation_fee_value, currency, webhook_secret')
        .eq('id', merchantId)
        .single();

      if (!error && data) {
        setPreferences({
          store_name: data.store_name ?? null,
          email_store_name: data.email_store_name ?? null,
          default_deadline_hours: data.default_deadline_hours ?? 72,
          default_tax_rate: data.default_tax_rate ?? 0,
          minimum_split_order_value: Number((data as any).minimum_split_order_value ?? 0),
          customer_pan_collection_enabled: (data as any).customer_pan_collection_enabled !== false,
          customer_pan_required: (data as any).customer_pan_required ?? false,
          split_discount_enabled: (data as any).split_discount_enabled ?? false,
          split_discount_percentage: (data as any).split_discount_percentage ?? null,
          split_discount_cap: (data as any).split_discount_cap ?? null,
          widget_installed: data.widget_installed ?? false,
          widget_position: data.widget_position ?? 'image-top-left',
          brand_color: data.brand_color ?? '#8B5CF6',
          split_button_color: (data as any).split_button_color ?? '#3F51B5',
          split_button_text: (data as any).split_button_text ?? 'Split with Friends',
          split_card_color: (data as any).split_card_color ?? '#F9FAFB',
          storefront_help_display: ((data as any).storefront_help_display as 'none' | 'link' | 'bubble' | null) ?? 'none',
          storefront_show_product_button: (data as any).storefront_show_product_button ?? true,
          storefront_show_cart_button: (data as any).storefront_show_cart_button ?? true,
          storefront_show_mini_cart_button: (data as any).storefront_show_mini_cart_button ?? true,
          storefront_required_cart_attributes: Array.isArray(data.storefront_required_cart_attributes)
            ? data.storefront_required_cart_attributes
            : [],
          storefront_cart_attribute_rules: normalizeCartAttributeRules(data.storefront_cart_attribute_rules),
          storefront_cart_validation_message: data.storefront_cart_validation_message ?? null,
          auto_enable_new_products: data.auto_enable_new_products ?? false,
          cancellation_fee_type: (data.cancellation_fee_type as 'none' | 'fixed' | 'percentage') ?? 'none',
          cancellation_fee_value: data.cancellation_fee_value ?? 0,
          currency: data.currency ?? 'USD',
          webhook_secret: (data as any).webhook_secret ?? null,
        });
        setRequiredCartAttributesText(
          Array.isArray(data.storefront_required_cart_attributes)
            ? data.storefront_required_cart_attributes.join("\n")
            : "",
        );
      }

      // Fetch report preferences
      const { data: reportData, error: reportError } = await supabase
        .from("merchant_report_preferences")
        .select("*")
        .eq("merchant_id", merchantId)
        .maybeSingle();

      if (!reportError && reportData) {
        setReportPreferences({
          id: reportData.id,
          email_reports_enabled: reportData.email_reports_enabled,
          report_frequency: reportData.report_frequency as "weekly" | "monthly",
          report_day: reportData.report_day,
          report_email: reportData.report_email,
          last_report_sent_at: reportData.last_report_sent_at,
        });
      }

      // Fetch notification preferences
      const { data: notifData, error: notifError } = await supabase
        .from("merchant_notification_preferences")
        .select("*")
        .eq("merchant_id", merchantId)
        .maybeSingle();

      if (!notifError && notifData) {
        setNotificationPreferences({
          id: notifData.id,
          notify_new_order: notifData.notify_new_order,
          notify_payment_received: notifData.notify_payment_received,
          notify_order_completed: notifData.notify_order_completed,
          notify_order_expired: notifData.notify_order_expired,
          notify_refund_requested: notifData.notify_refund_requested,
          notify_low_participation: notifData.notify_low_participation,
          digest_mode: notifData.digest_mode as 'instant' | 'daily' | 'weekly',
          digest_time: notifData.digest_time?.slice(0, 5) || '09:00',
          notification_email: notifData.notification_email,
          notification_phone: notifData.notification_phone,
          whatsapp_enabled: notifData.whatsapp_enabled,
          reminder_enabled: notifData.reminder_enabled ?? true,
          daily_reminder_enabled: (notifData as any).daily_reminder_enabled ?? false,
          one_hour_reminder_enabled: (notifData as any).one_hour_reminder_enabled ?? true,
          additional_reminder_enabled: (notifData as any).additional_reminder_enabled ?? false,
          additional_reminder_hours: (notifData as any).additional_reminder_hours ?? 12,
        });
      }
    } catch (error) {
      console.error("Error fetching preferences:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveReportPreferences = async () => {
    setSavingReportPrefs(true);
    try {
      const payload = {
        merchant_id: merchantId,
        email_reports_enabled: reportPreferences.email_reports_enabled,
        report_frequency: reportPreferences.report_frequency,
        report_day: reportPreferences.report_day,
        report_email: reportPreferences.report_email || null,
      };

      if (reportPreferences.id) {
        const { error } = await supabase
          .from("merchant_report_preferences")
          .update(payload)
          .eq("id", reportPreferences.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("merchant_report_preferences")
          .insert(payload)
          .select()
          .single();
        if (error) throw error;
        setReportPreferences(prev => ({ ...prev, id: data.id }));
      }

      toast.success("Report preferences saved!");
    } catch (error: any) {
      console.error("Error saving report preferences:", error);
      toast.error("Failed to save report preferences");
    } finally {
      setSavingReportPrefs(false);
    }
  };

  const saveNotificationPreferences = async () => {
    setSavingNotificationPrefs(true);
    try {
      const payload = {
        merchant_id: merchantId,
        notify_new_order: notificationPreferences.notify_new_order,
        notify_payment_received: notificationPreferences.notify_payment_received,
        notify_order_completed: notificationPreferences.notify_order_completed,
        notify_order_expired: notificationPreferences.notify_order_expired,
        notify_refund_requested: notificationPreferences.notify_refund_requested,
        notify_low_participation: notificationPreferences.notify_low_participation,
        digest_mode: notificationPreferences.digest_mode,
        digest_time: notificationPreferences.digest_time + ':00',
        notification_email: notificationPreferences.notification_email || null,
        notification_phone: notificationPreferences.notification_phone || null,
        whatsapp_enabled: notificationPreferences.whatsapp_enabled,
        reminder_enabled: notificationPreferences.reminder_enabled,
        daily_reminder_enabled: notificationPreferences.daily_reminder_enabled,
        one_hour_reminder_enabled: notificationPreferences.one_hour_reminder_enabled,
        additional_reminder_enabled: notificationPreferences.additional_reminder_enabled,
        additional_reminder_hours: notificationPreferences.additional_reminder_hours,
      } as any;

      if (notificationPreferences.id) {
        const { error } = await supabase
          .from("merchant_notification_preferences")
          .update(payload)
          .eq("id", notificationPreferences.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("merchant_notification_preferences")
          .insert(payload)
          .select()
          .single();
        if (error) throw error;
        setNotificationPreferences(prev => ({ ...prev, id: data.id }));
      }

      toast.success("Notification preferences saved!");
    } catch (error: any) {
      console.error("Error saving notification preferences:", error);
      toast.error("Failed to save notification preferences");
    } finally {
      setSavingNotificationPrefs(false);
    }
  };

  const sendTestReminder = async (reminderType: 'midpoint' | 'final') => {
    setSendingTestReminder(reminderType);
    try {
      const response = await fetchSupabaseFunction("send-test-reminder", {
        body: { merchant_id: merchantId, reminder_type: reminderType },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to send test reminder");
      }

      toast.success(`Test ${reminderType === 'final' ? 'final' : 'first'} reminder sent to ${result.email}!`);
    } catch (error: any) {
      console.error("Error sending test reminder:", error);
      toast.error(error.message || "Failed to send test reminder");
    } finally {
      setSendingTestReminder(null);
    }
  };

  const sendTestReport = async () => {
    setSendingTestReport(true);
    try {
      const response = await fetchSupabaseFunction("send-analytics-report", {
        body: { merchant_id: merchantId, force: true },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to send report");
      }

      toast.success("Test report sent! Check your email.");
    } catch (error: any) {
      console.error("Error sending test report:", error);
      toast.error(error.message || "Failed to send test report");
    } finally {
      setSendingTestReport(false);
    }
  };

  const handleSyncMetafieldAccess = async () => {
    setSyncingMetafield(true);
    try {
      const { data, error } = await invokeShopifyEmbeddedFunction("fix-metafield-access", {
        body: { merchantId },
      });

      if (error) throw error;

      if (data.success) {
        toast.success(data.message || "Metafield storefront access synced!");
      } else {
        toast.error(data.message || "Failed to sync metafield access");
      }
    } catch (error: any) {
      console.error("Error syncing metafield access:", error);
      toast.error(error.message || "Failed to sync metafield access");
    } finally {
      setSyncingMetafield(false);
    }
  };

  // Team management functions
  const fetchTeamData = async () => {
    try {
      const { data: members, error: membersError } = await supabase
        .from('merchant_users')
        .select('id, user_id, role, created_at')
        .eq('merchant_id', merchantId);

      if (membersError) throw membersError;

      const { data: invites, error: invitesError } = await supabase
        .from('merchant_invitations')
        .select('id, email, role, status, created_at, expires_at')
        .eq('merchant_id', merchantId)
        .eq('status', 'pending');

      if (invitesError) throw invitesError;

      setTeamMembers(members || []);
      setInvitations(invites || []);
    } catch (error: any) {
      console.error("Error fetching team data:", error);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail || !inviteRole) {
      toast.error("Please fill in all fields");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSendingInvite(true);
    try {
      const { data, error } = await invokeShopifyEmbeddedFunction('send-team-invitation', {
        body: {
          merchantId,
          email: inviteEmail.toLowerCase().trim(),
          role: inviteRole
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      setInviteRole("view_only");
      setInviteDialogOpen(false);
      fetchTeamData();
    } catch (error: any) {
      console.error("Error sending invite:", error);
      toast.error(error.message || "Failed to send invitation");
    } finally {
      setSendingInvite(false);
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    setRevokingInviteId(invitationId);
    try {
      const { error } = await supabase
        .from('merchant_invitations')
        .update({ status: 'revoked' })
        .eq('id', invitationId);

      if (error) throw error;

      toast.success("Invitation revoked");
      fetchTeamData();
    } catch (error: any) {
      console.error("Error revoking invitation:", error);
      toast.error("Failed to revoke invitation");
    } finally {
      setRevokingInviteId(null);
    }
  };

  const handleRemoveMember = async (memberId: string, role: string) => {
    if (role === 'super_admin') {
      toast.error(`Cannot remove the ${ownerLabel.toLowerCase()}`);
      return;
    }

    setRemovingMemberId(memberId);
    try {
      const { error } = await supabase
        .from('merchant_users')
        .delete()
        .eq('id', memberId);

      if (error) throw error;

      toast.success("Team member removed");
      fetchTeamData();
    } catch (error: any) {
      console.error("Error removing member:", error);
      toast.error("Failed to remove team member");
    } finally {
      setRemovingMemberId(null);
    }
  };

  const handleUpdateMemberRole = async (memberId: string, newRole: string, currentRole: string) => {
    if (currentRole === 'super_admin') {
      toast.error(`Cannot change the ${ownerLabel.toLowerCase()}'s role`);
      return;
    }

    try {
      const { error } = await supabase
        .from('merchant_users')
        .update({ role: newRole })
        .eq('id', memberId);

      if (error) throw error;

      toast.success("Role updated successfully");
      fetchTeamData();
    } catch (error: any) {
      console.error("Error updating role:", error);
      toast.error("Failed to update role");
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'super_admin': return 'Owner';
      case 'manager': return 'Manager';
      case 'view_only': return 'View Only';
      default: return role;
    }
  };

  const getRoleBadgeVariant = (role: string): "default" | "secondary" | "outline" => {
    switch (role) {
      case 'super_admin': return 'default';
      case 'manager': return 'secondary';
      default: return 'outline';
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchTeamData();
    }
  }, [merchantId, isSuperAdmin]);

  const effectiveEmail = reportPreferences.report_email || merchantEmail || "Not set";
  const scheduleDescription = reportPreferences.report_frequency === "weekly"
    ? `Every ${DAYS_OF_WEEK.find(d => d.value === reportPreferences.report_day)?.label || "Monday"}`
    : `On the ${DAYS_OF_MONTH.find(d => d.value === reportPreferences.report_day)?.label || "1st"} of each month`;

  const handleSave = async (field: keyof PreferenceData, value: any) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('merchants')
        .update({ [field]: value })
        .eq('id', merchantId);

      if (error) throw error;
      
      setPreferences(prev => ({ ...prev, [field]: value }));
      toast.success("Preference saved");
    } catch (error: any) {
      console.error("Error saving preference:", error);
      toast.error("Failed to save preference");
    } finally {
      setSaving(false);
    }
  };

  const updateCartAttributeRule = (
    ruleId: string,
    updater: (rule: CartAttributeRule) => CartAttributeRule,
  ) => {
    setPreferences((previous) => ({
      ...previous,
      storefront_cart_attribute_rules: previous.storefront_cart_attribute_rules.map((rule) =>
        rule.id === ruleId ? updater(rule) : rule
      ),
    }));
  };

  const addCartAttributeRule = () => {
    if (preferences.storefront_cart_attribute_rules.length >= 25) {
      toast.error("A maximum of 25 conditional rules is supported.");
      return;
    }

    setPreferences((previous) => ({
      ...previous,
      storefront_cart_attribute_rules: [
        ...previous.storefront_cart_attribute_rules,
        createEmptyCartAttributeRule(),
      ],
    }));
  };

  const saveCartAttributeRules = async () => {
    const rules = preferences.storefront_cart_attribute_rules;
    const incompleteRule = rules.find((rule) => {
      if (!rule.name.trim() || rule.conditions.length === 0 || rule.requiredAttributes.length === 0) {
        return true;
      }

      return rule.conditions.some((condition) => {
        if (condition.source === 'selector') {
          if (!condition.selector.trim()) return true;
          const readMode = SELECTOR_READ_OPTIONS.find((option) => option.value === condition.selectorRead);
          if (readMode?.needsKey && !condition.selectorReadKey.trim()) return true;
        } else if (!condition.attribute.trim()) {
          return true;
        }
        const operator = CART_RULE_OPERATOR_OPTIONS.find((option) => option.value === condition.operator);
        return operator?.needsValue && !condition.value.trim();
      });
    });

    if (incompleteRule) {
      toast.error(`Complete the conditions and required attributes for "${incompleteRule.name || 'Untitled rule'}".`);
      return;
    }

    const normalizedRules = normalizeCartAttributeRules(rules);
    await handleSave('storefront_cart_attribute_rules', normalizedRules);
  };

  const handlePositionChange = async (value: string) => {
    setPreferences(prev => ({ ...prev, widget_position: value }));
    await handleSave('widget_position', value);
  };

  const handleInstallWidget = async () => {
    if (isWooCommerceMerchant) {
      setPreferences(prev => ({ ...prev, widget_installed: true }));
      toast.success("Connector details are ready. Save the Merchant Token in WooCommerce to complete setup.");
      return;
    }

    setInstallingWidget(true);
    try {
      const { data, error } = await invokeShopifyEmbeddedFunction('install-widget', {
        body: { 
          merchantId,
          position: preferences.widget_position,
        },
      });

      if (error) throw error;
      
      if (data?.success) {
        if (data.requiresThemeExtension) {
          toast.success(data.message || "Enable the Splitkart app embed in Shopify Theme Editor to finish setup.");
        } else {
          setPreferences(prev => ({ ...prev, widget_installed: true }));
          toast.success("Widget installed successfully!");
        }
      } else {
        toast.error(data?.error || "Failed to install widget");
      }
    } catch (error: any) {
      console.error("Error installing widget:", error);
      toast.error(error.message || "Failed to install widget");
    } finally {
      setInstallingWidget(false);
    }
  };

  const fetchUninstallStats = async () => {
    setLoadingStats(true);
    try {
      const { data: requests, error } = await supabase
        .from('split_payment_requests')
        .select('status, total_price')
        .eq('merchant_id', merchantId);

      if (error) throw error;

      const stats: UninstallStats = {
        totalRequests: requests?.length || 0,
        completedRequests: requests?.filter(r => r.status === 'completed').length || 0,
        pendingRequests: requests?.filter(r => r.status === 'pending').length || 0,
        totalRevenue: requests?.filter(r => r.status === 'completed').reduce((sum, r) => sum + (r.total_price || 0), 0) || 0,
        pendingRevenue: requests?.filter(r => r.status === 'pending').reduce((sum, r) => sum + (r.total_price || 0), 0) || 0,
      };

      setUninstallStats(stats);
    } catch (error) {
      console.error("Error fetching uninstall stats:", error);
      setUninstallStats({
        totalRequests: 0,
        completedRequests: 0,
        pendingRequests: 0,
        totalRevenue: 0,
        pendingRevenue: 0,
      });
    } finally {
      setLoadingStats(false);
    }
  };

  const handleUninstallClick = async () => {
    await fetchUninstallStats();
    setShowUninstallDialog(true);
  };

  const handleUninstallWidget = async () => {
    if (isWooCommerceMerchant) {
      setPreferences(prev => ({ ...prev, widget_installed: false }));
      toast.success("WooCommerce connector marked as not installed");
      setShowUninstallDialog(false);
      return;
    }

    setUninstallingWidget(true);
    try {
      const { data, error } = await invokeShopifyEmbeddedFunction('install-widget', {
        body: { 
          merchantId,
          action: 'uninstall',
        },
      });

      if (error) throw error;
      
      if (data?.success) {
        if (data.requiresThemeExtension) {
          toast.success(data.message || "Disable the app embed from Shopify Theme Editor to remove storefront widgets.");
        } else {
          setPreferences(prev => ({ ...prev, widget_installed: false }));
          toast.success("Widget uninstalled successfully");
          setShowUninstallDialog(false);
        }
      } else {
        toast.error(data?.error || "Failed to uninstall widget");
      }
    } catch (error: any) {
      console.error("Error uninstalling widget:", error);
      toast.error(error.message || "Failed to uninstall widget");
    } finally {
      setUninstallingWidget(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Accordion type="multiple" defaultValue={[]} className="space-y-3">
        {/* Widget & Integration */}
        <AccordionItem value="widget" className="border rounded-lg px-4 bg-card">
          <AccordionTrigger className="hover:no-underline py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <ShoppingCart className="h-4 w-4 text-primary" />
              </div>
              <div className="text-left">
                <p className="font-medium">Split Payment Widget</p>
                <p className="text-xs text-muted-foreground font-normal">
                  {preferences.widget_installed ? 'Active on cart page' : 'Not installed'}
                </p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-4">
            {isWooCommerceMerchant ? (
              <div className="space-y-4">
                <Alert className="border-primary/30 bg-primary/5">
                  <AlertDescription className="text-sm text-muted-foreground">
                    WooCommerce connector details now live under <strong>Settings → Connector</strong>. Use that page to copy the Merchant Token, Webhook Secret, and store details after login.
                  </AlertDescription>
                </Alert>
                <Button variant="outline" onClick={() => navigate("/merchant-dashboard?tab=settings&settingsView=connector")}>
                  Open Connector Settings
                </Button>
              </div>
            ) : (
              <>

            {/* New cart-based widget info */}
            <div className="space-y-3">
              <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                <div className="flex items-start gap-3">
                  <ShoppingCart className="h-5 w-5 text-primary mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Store-wide Split Payments</p>
                    <p className="text-xs text-muted-foreground">
                      A "Split your payment with friends" button appears on your cart page before checkout. 
                      Customers can add any products to their cart and then choose to split the total with friends.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 rounded-lg bg-muted/50 border space-y-4">
                <div className="flex items-start gap-3">
                  <Bell className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Homepage Banner</p>
                    <p className="text-xs text-muted-foreground">
                      A promotional banner is shown on your homepage to let customers know split payments are available.
                    </p>
                  </div>
                </div>

                {/* Visual Preview */}
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Preview</Label>
                  <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 rounded-lg h-48 border-2 border-dashed border-gray-300 dark:border-gray-600 overflow-hidden">
                    {/* Simulated page content */}
                    <div className="absolute inset-4 space-y-2">
                      <div className="h-3 w-24 bg-gray-300 dark:bg-gray-700 rounded" />
                      <div className="h-2 w-40 bg-gray-300 dark:bg-gray-700 rounded" />
                      <div className="h-2 w-32 bg-gray-300 dark:bg-gray-700 rounded" />
                      <div className="mt-4 grid grid-cols-3 gap-2">
                        <div className="h-12 bg-gray-300 dark:bg-gray-700 rounded" />
                        <div className="h-12 bg-gray-300 dark:bg-gray-700 rounded" />
                        <div className="h-12 bg-gray-300 dark:bg-gray-700 rounded" />
                      </div>
                    </div>
                    
                    {/* Banner Preview */}
                    {preferences.widget_position !== 'disabled' && (
                      <div 
                        className={`absolute max-w-[200px] p-2 text-white rounded-lg shadow-lg text-[10px] flex items-center gap-2
                          ${preferences.widget_position === 'top-left' ? 'top-2 left-2' : ''}
                          ${preferences.widget_position === 'top-right' ? 'top-2 right-2' : ''}
                          ${preferences.widget_position === 'bottom-right' ? 'bottom-2 right-2' : ''}
                          ${preferences.widget_position === 'bottom-left' || !preferences.widget_position ? 'bottom-2 left-2' : ''}
                        `}
                        style={{ backgroundColor: preferences.split_button_color || '#3F51B5', color: getContrastTextColor(preferences.split_button_color || '#3F51B5') }}
                      >
                        <div className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}>
                          <Users className="h-3 w-3" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold leading-tight">Split payments available!</p>
                          <p className="opacity-90 leading-tight truncate">You can now split payments for group purchases on {preferences.store_name || 'your store'}</p>
                        </div>
                        <div
                          className="flex h-5 w-5 items-center justify-center rounded-full border text-[9px] font-bold"
                          style={{ borderColor: 'rgba(255,255,255,0.28)', backgroundColor: 'rgba(255,255,255,0.22)' }}
                        >
                          x
                        </div>
                      </div>
                    )}
                    
                    {preferences.widget_position === 'disabled' && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center text-muted-foreground">
                          <Ban className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-xs">Banner disabled</p>
                        </div>
                      </div>
                    )}

                    {/* Position indicator label */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/80 dark:bg-black/60 px-2 py-1 rounded text-[10px] text-muted-foreground">
                      Your Homepage
                    </div>
                  </div>
                  
                  {/* Exact Banner Text */}
                  {preferences.widget_position !== 'disabled' && (
                    <div className="mt-3 p-3 bg-white dark:bg-gray-900 rounded-lg border space-y-2">
                      <Label className="text-xs text-muted-foreground">Exact Banner Text</Label>
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-foreground">"Split payments available!"</p>
                        <p className="text-xs text-muted-foreground italic">
                          "You can now split payments for group purchases on {preferences.store_name || '[Your Store Name]'}"
                        </p>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        Banner auto-dismisses after 10 seconds • Reappears after 24 hours if closed
                      </p>
                    </div>
                  )}
                </div>
                
                {canEdit && (
                  <div className="pt-2 border-t space-y-4">
                    <div>
                      <Label className="text-xs text-muted-foreground mb-2 block">Banner Position</Label>
                      <Select 
                        value={preferences.widget_position || 'bottom-left'} 
                        onValueChange={handlePositionChange}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select position" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bottom-left">Bottom Left</SelectItem>
                          <SelectItem value="bottom-right">Bottom Right</SelectItem>
                          <SelectItem value="top-left">Top Left</SelectItem>
                          <SelectItem value="top-right">Top Right</SelectItem>
                          <SelectItem value="disabled">Disabled (Don't show banner)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Split Button And Popup Color</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={preferences.split_button_color || '#3F51B5'}
                            onChange={(event) => setPreferences(prev => ({ ...prev, split_button_color: event.target.value }))}
                            onBlur={(event) => handleSave('split_button_color', event.target.value)}
                            className="h-9 w-14 p-1"
                            disabled={!canEdit || saving}
                          />
                          <Input
                            value={preferences.split_button_color || '#3F51B5'}
                            onChange={(event) => setPreferences(prev => ({ ...prev, split_button_color: event.target.value }))}
                            onBlur={(event) => handleSave('split_button_color', event.target.value || '#3F51B5')}
                            className="h-9"
                            disabled={!canEdit || saving}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Split Card Color</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={preferences.split_card_color || '#F9FAFB'}
                            onChange={(event) => setPreferences(prev => ({ ...prev, split_card_color: event.target.value }))}
                            onBlur={(event) => handleSave('split_card_color', event.target.value)}
                            className="h-9 w-14 p-1"
                            disabled={!canEdit || saving}
                          />
                          <Input
                            value={preferences.split_card_color || '#F9FAFB'}
                            onChange={(event) => setPreferences(prev => ({ ...prev, split_card_color: event.target.value }))}
                            onBlur={(event) => handleSave('split_card_color', event.target.value || '#F9FAFB')}
                            className="h-9"
                            disabled={!canEdit || saving}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Split Button Text</Label>
                      <Input
                        value={preferences.split_button_text || 'Split with Friends'}
                        onChange={(event) => setPreferences(prev => ({ ...prev, split_button_text: event.target.value }))}
                        onBlur={(event) => handleSave('split_button_text', event.target.value.trim() || 'Split with Friends')}
                        placeholder="Split with Friends"
                        maxLength={48}
                        disabled={!canEdit || saving}
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Example: use “Split this order” if you want a more direct button label on product and cart pages.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Storefront Help Display</Label>
                      <Select
                        value={preferences.storefront_help_display || 'none'}
                        onValueChange={(value: 'none' | 'link' | 'bubble') => {
                          setPreferences(prev => ({ ...prev, storefront_help_display: value }));
                          void handleSave('storefront_help_display', value);
                        }}
                        disabled={!canEdit || saving}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Choose storefront help display" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Off</SelectItem>
                          <SelectItem value="link">How this works link below button</SelectItem>
                          <SelectItem value="bubble">Hover bubble next to button</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-muted-foreground">
                        Adds storefront guidance right beside the split button so shoppers understand the flow before clicking.
                      </p>
                    </div>

                    <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
                      <div className="space-y-1">
                        <Label className="text-xs font-medium">Show Split Button On</Label>
                        <p className="text-[11px] text-muted-foreground">
                          Choose where the storefront split button appears. All placements are enabled by default.
                        </p>
                      </div>
                      {[
                        {
                          key: 'storefront_show_product_button',
                          label: 'Product page',
                          description: 'Show beside Buy Now or Add to Cart on product pages.',
                        },
                        {
                          key: 'storefront_show_mini_cart_button',
                          label: 'Mini Cart',
                          description: 'Show inside cart drawers and mini-cart popups.',
                        },
                        {
                          key: 'storefront_show_cart_button',
                          label: 'Cart',
                          description: 'Show on the full cart page near checkout.',
                        },
                      ].map((item) => (
                        <div key={item.key} className="flex items-center justify-between gap-4 rounded-md bg-white p-3">
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-slate-900">{item.label}</p>
                            <p className="text-[11px] text-muted-foreground">{item.description}</p>
                          </div>
                          <Switch
                            checked={(preferences[item.key as keyof PreferenceData] as boolean | null) !== false}
                            onCheckedChange={(checked) => {
                              setPreferences(prev => ({ ...prev, [item.key]: checked }));
                              void handleSave(item.key as keyof PreferenceData, checked);
                            }}
                            disabled={!canEdit || saving}
                            aria-label={`Show split button on ${item.label}`}
                          />
                        </div>
                      ))}
                    </div>

                    <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
                      <div className="space-y-1">
                        <Label className="text-xs font-medium">Always Required Cart Attributes</Label>
                        <p className="text-[11px] text-muted-foreground">
                          Enter attributes that every split order must contain, regardless of the customer's selections.
                        </p>
                      </div>
                      <Textarea
                        value={requiredCartAttributesText}
                        onChange={(event) => {
                          setRequiredCartAttributesText(event.target.value);
                        }}
                        onBlur={(event) => {
                          const attributes = parseCartAttributeLines(event.target.value);
                          const normalizedText = attributes.join("\n");
                          setRequiredCartAttributesText(normalizedText);
                          setPreferences((prev) => ({
                            ...prev,
                            storefront_required_cart_attributes: attributes,
                          }));
                          handleSave('storefront_required_cart_attributes', attributes);
                        }}
                        placeholder={"Delivery Method\norder due date\norder time"}
                        className="min-h-[92px]"
                        disabled={!canEdit || saving}
                      />
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Blocked Message</Label>
                        <Input
                          value={preferences.storefront_cart_validation_message || ""}
                          onChange={(event) => setPreferences((prev) => ({
                            ...prev,
                            storefront_cart_validation_message: event.target.value,
                          }))}
                          onBlur={(event) => handleSave(
                            'storefront_cart_validation_message',
                            event.target.value.trim() || null,
                          )}
                          placeholder="Please select delivery method, date, and time before splitting this order."
                          disabled={!canEdit || saving}
                        />
                      </div>
                    </div>

                    <div className="rounded-lg border bg-muted/30 p-3 space-y-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <GitBranch className="h-4 w-4 text-primary" />
                            <Label className="text-xs font-medium">Conditional Storefront Rules</Label>
                          </div>
                          <p className="max-w-2xl text-[11px] text-muted-foreground">
                            Match Shopify cart attributes or storefront elements. Existing cart-attribute rules continue to work unchanged.
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addCartAttributeRule}
                          disabled={!canEdit || saving || preferences.storefront_cart_attribute_rules.length >= 25}
                        >
                          <Plus className="mr-1.5 h-3.5 w-3.5" />
                          Add Rule
                        </Button>
                      </div>

                      {preferences.storefront_cart_attribute_rules.length === 0 ? (
                        <div className="rounded-md border border-dashed bg-white px-4 py-6 text-center">
                          <p className="text-sm font-medium text-slate-800">No conditional rules</p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            Add a rule when different customer selections require different cart attributes.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {preferences.storefront_cart_attribute_rules.map((rule, ruleIndex) => (
                            <div key={rule.id} className="space-y-4 rounded-lg border bg-white p-4">
                              <div className="flex items-center gap-3">
                                <Input
                                  value={rule.name}
                                  onChange={(event) => updateCartAttributeRule(rule.id, (current) => ({
                                    ...current,
                                    name: event.target.value.slice(0, 200),
                                  }))}
                                  placeholder={`Rule ${ruleIndex + 1}`}
                                  className="font-medium"
                                  disabled={!canEdit || saving}
                                />
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">
                                    {rule.enabled ? 'Enabled' : 'Disabled'}
                                  </span>
                                  <Switch
                                    checked={rule.enabled}
                                    onCheckedChange={(enabled) => updateCartAttributeRule(rule.id, (current) => ({
                                      ...current,
                                      enabled,
                                    }))}
                                    disabled={!canEdit || saving}
                                    aria-label={`${rule.enabled ? 'Disable' : 'Enable'} ${rule.name}`}
                                  />
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setPreferences((previous) => ({
                                    ...previous,
                                    storefront_cart_attribute_rules: previous.storefront_cart_attribute_rules.filter(
                                      (candidate) => candidate.id !== rule.id
                                    ),
                                  }))}
                                  disabled={!canEdit || saving}
                                  aria-label={`Delete ${rule.name}`}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>

                              <div className="space-y-2">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <Label className="text-xs">Apply this rule when</Label>
                                  <Select
                                    value={rule.match}
                                    onValueChange={(match: 'all' | 'any') => updateCartAttributeRule(rule.id, (current) => ({
                                      ...current,
                                      match,
                                    }))}
                                    disabled={!canEdit || saving}
                                  >
                                    <SelectTrigger className="h-8 w-[190px]">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="all">All conditions match</SelectItem>
                                      <SelectItem value="any">Any condition matches</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>

                                {rule.conditions.map((condition, conditionIndex) => {
                                  const operator = CART_RULE_OPERATOR_OPTIONS.find(
                                    (option) => option.value === condition.operator
                                  );
                                  const selectorRead = SELECTOR_READ_OPTIONS.find(
                                    (option) => option.value === condition.selectorRead
                                  );

                                  return (
                                    <div
                                      key={`${rule.id}-condition-${conditionIndex}`}
                                      className="space-y-2 rounded-md bg-muted/40 p-2"
                                    >
                                      <div className="grid gap-2 md:grid-cols-[180px_1fr_auto]">
                                        <Select
                                          value={condition.source}
                                          onValueChange={(source: CartRuleConditionSource) => updateCartAttributeRule(rule.id, (current) => ({
                                            ...current,
                                            conditions: current.conditions.map((candidate, index) =>
                                              index === conditionIndex ? { ...candidate, source } : candidate
                                            ),
                                          }))}
                                          disabled={!canEdit || saving}
                                        >
                                          <SelectTrigger>
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="cart_attribute">Cart Attribute</SelectItem>
                                            <SelectItem value="selector">Page Selector</SelectItem>
                                          </SelectContent>
                                        </Select>
                                        {condition.source === 'selector' ? (
                                          <Input
                                            value={condition.selector}
                                            onChange={(event) => updateCartAttributeRule(rule.id, (current) => ({
                                              ...current,
                                              conditions: current.conditions.map((candidate, index) =>
                                                index === conditionIndex
                                                  ? { ...candidate, selector: event.target.value.slice(0, 500) }
                                                  : candidate
                                              ),
                                            }))}
                                            placeholder="CSS selector, e.g. #tab-delivery"
                                            disabled={!canEdit || saving}
                                          />
                                        ) : (
                                          <Input
                                            value={condition.attribute}
                                            onChange={(event) => updateCartAttributeRule(rule.id, (current) => ({
                                              ...current,
                                              conditions: current.conditions.map((candidate, index) =>
                                                index === conditionIndex
                                                  ? { ...candidate, attribute: event.target.value.slice(0, 200) }
                                                  : candidate
                                              ),
                                            }))}
                                            placeholder="Cart attribute name"
                                            disabled={!canEdit || saving}
                                          />
                                        )}
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => updateCartAttributeRule(rule.id, (current) => ({
                                            ...current,
                                            conditions: current.conditions.filter((_, index) => index !== conditionIndex),
                                          }))}
                                          disabled={!canEdit || saving || rule.conditions.length === 1}
                                          aria-label="Remove condition"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>

                                      {condition.source === 'selector' && (
                                        <div className="grid gap-2 md:grid-cols-2">
                                          <Select
                                            value={condition.selectorRead}
                                            onValueChange={(nextRead: SelectorReadMode) => updateCartAttributeRule(rule.id, (current) => ({
                                              ...current,
                                              conditions: current.conditions.map((candidate, index) =>
                                                index === conditionIndex
                                                  ? {
                                                      ...candidate,
                                                      selectorRead: nextRead,
                                                      selectorReadKey: SELECTOR_READ_OPTIONS.find(
                                                        (option) => option.value === nextRead
                                                      )?.needsKey
                                                        ? candidate.selectorReadKey
                                                        : '',
                                                    }
                                                  : candidate
                                              ),
                                            }))}
                                            disabled={!canEdit || saving}
                                          >
                                            <SelectTrigger>
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {SELECTOR_READ_OPTIONS.map((option) => (
                                                <SelectItem key={option.value} value={option.value}>
                                                  {option.label}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                          {selectorRead?.needsKey ? (
                                            <Input
                                              value={condition.selectorReadKey}
                                              onChange={(event) => updateCartAttributeRule(rule.id, (current) => ({
                                                ...current,
                                                conditions: current.conditions.map((candidate, index) =>
                                                  index === conditionIndex
                                                    ? { ...candidate, selectorReadKey: event.target.value.slice(0, 200) }
                                                    : candidate
                                                ),
                                              }))}
                                              placeholder={condition.selectorRead === 'class' ? 'CSS class name' : 'Attribute name, e.g. aria-selected'}
                                              disabled={!canEdit || saving}
                                            />
                                          ) : (
                                            <div className="flex items-center rounded-md border bg-background px-3 text-xs text-muted-foreground">
                                              No attribute or class name needed
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      <div className="grid gap-2 md:grid-cols-[180px_1fr]">
                                        <Select
                                          value={condition.operator}
                                          onValueChange={(nextOperator: CartRuleOperator) => updateCartAttributeRule(rule.id, (current) => ({
                                            ...current,
                                            conditions: current.conditions.map((candidate, index) =>
                                              index === conditionIndex
                                                ? {
                                                    ...candidate,
                                                    operator: nextOperator,
                                                    value: ['has_value', 'is_empty'].includes(nextOperator)
                                                      ? ''
                                                      : candidate.value,
                                                  }
                                                : candidate
                                            ),
                                          }))}
                                          disabled={!canEdit || saving}
                                        >
                                          <SelectTrigger>
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {CART_RULE_OPERATOR_OPTIONS.map((option) => (
                                              <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        {operator?.needsValue ? (
                                        <Input
                                          value={condition.value}
                                          onChange={(event) => updateCartAttributeRule(rule.id, (current) => ({
                                            ...current,
                                            conditions: current.conditions.map((candidate, index) =>
                                              index === conditionIndex
                                                ? { ...candidate, value: event.target.value.slice(0, 500) }
                                                : candidate
                                            ),
                                          }))}
                                          placeholder={condition.operator === 'one_of' ? 'Value 1, Value 2' : 'Expected value'}
                                          disabled={!canEdit || saving}
                                        />
                                        ) : (
                                          <div className="flex items-center rounded-md border bg-background px-3 text-xs text-muted-foreground">
                                            No expected value needed
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}

                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => updateCartAttributeRule(rule.id, (current) => ({
                                    ...current,
                                    conditions: [
                                      ...current.conditions,
                                      createEmptyCartRuleCondition(),
                                    ],
                                  }))}
                                  disabled={!canEdit || saving || rule.conditions.length >= 10}
                                >
                                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                                  Add Condition
                                </Button>
                              </div>

                              <div className="grid gap-3 md:grid-cols-2">
                                <div className="space-y-1">
                                  <Label className="text-xs">Required attributes when matched</Label>
                                  <Textarea
                                    defaultValue={rule.requiredAttributes.join('\n')}
                                    onBlur={(event) => updateCartAttributeRule(rule.id, (current) => ({
                                      ...current,
                                      requiredAttributes: parseCartAttributeLines(event.target.value).slice(0, 20),
                                    }))}
                                    placeholder={"Collection Location\nCollection Date\nCollection Time"}
                                    className="min-h-[92px]"
                                    disabled={!canEdit || saving}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Rule-specific blocked message</Label>
                                  <Textarea
                                    value={rule.message}
                                    onChange={(event) => updateCartAttributeRule(rule.id, (current) => ({
                                      ...current,
                                      message: event.target.value.slice(0, 500),
                                    }))}
                                    placeholder="Please complete the required details for your selected option."
                                    className="min-h-[92px]"
                                    disabled={!canEdit || saving}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}

                        </div>
                      )}

                      <div className="flex justify-end">
                        <Button
                          type="button"
                          onClick={() => void saveCartAttributeRules()}
                          disabled={!canEdit || saving}
                        >
                          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Save Conditional Rules
                        </Button>
                      </div>
                    </div>

                    <div
                      className="rounded-xl border p-4"
                      style={{ backgroundColor: preferences.split_card_color || '#F9FAFB' }}
                    >
                      <p className="mb-3 text-sm font-medium text-slate-900">Customer preview</p>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            className="flex-1 text-white hover:opacity-90"
                            style={{ backgroundColor: preferences.split_button_color || '#3F51B5' }}
                          >
                            <Users className="mr-2 h-4 w-4" />
                            {preferences.split_button_text || 'Split with Friends'}
                          </Button>
                          {preferences.storefront_help_display === 'bubble' && (
                            <div className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-700 shadow-sm">
                              ?
                            </div>
                          )}
                        </div>
                        {preferences.storefront_help_display === 'link' && (
                          <p className="text-xs font-medium text-primary underline underline-offset-2">
                            How this works
                          </p>
                        )}
                        {preferences.storefront_help_display === 'bubble' && (
                          <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600 shadow-sm">
                            One person starts the split, pays first, and invites friends with secure payment links.
                            <span className="ml-1 font-medium text-primary">How this works</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sync Status & Actions */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t">
              <div className="flex items-center gap-2">
                {preferences.widget_installed ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Active
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Not Installed
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">
                  {isWooCommerceMerchant
                    ? (preferences.widget_installed ? 'Connector details confirmed for WooCommerce' : 'Use the Merchant Token above to connect the WooCommerce plugin')
                    : (preferences.widget_installed ? 'Widget synced with your store' : 'Click install to add to your store')}
                </span>
              </div>
              
              {canEdit && (
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="ghost" onClick={handleInstallWidget} disabled={installingWidget}>
                    {installingWidget ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                    {preferences.widget_installed ? 'Reinstall' : 'Install Widget'}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => window.open('/merchant/widget-guide', '_blank')}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Manual Setup
                  </Button>
                </div>
              )}
            </div>
              </>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* Payment Deadline */}
        <AccordionItem value="payment" className="border rounded-lg px-4 bg-card">
          <AccordionTrigger className="hover:no-underline py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Clock className="h-4 w-4 text-primary" />
              </div>
              <div className="text-left">
                <p className="font-medium">Payment & Cancellation</p>
                <p className="text-xs text-muted-foreground font-normal">
                  {`Deadline: ${preferences.default_deadline_hours}h • Cancel fee: ${preferences.cancellation_fee_type === 'none' ? 'None' : preferences.cancellation_fee_type === 'fixed' ? formatCurrency(preferences.cancellation_fee_value ?? 0, (preferences.currency as any) || 'USD') : `${preferences.cancellation_fee_value}%`}`}
                </p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-4">
            {/* Payment Deadline */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3 text-muted-foreground" />
                <Label className="text-xs text-muted-foreground">Payment Deadline</Label>
              </div>
              <Select
                value={String(preferences.default_deadline_hours)}
                onValueChange={(value) => handleSave('default_deadline_hours', parseInt(value))}
                disabled={!canEdit || saving}
              >
                <SelectTrigger className="h-9 max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24">24 hours (1 day)</SelectItem>
                  <SelectItem value="48">48 hours (2 days)</SelectItem>
                  <SelectItem value="72">72 hours (3 days)</SelectItem>
                  <SelectItem value="120">120 hours (5 days)</SelectItem>
                  <SelectItem value="168">168 hours (7 days)</SelectItem>
                  <SelectItem value="336">336 hours (14 days)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Currency Selection */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Wallet className="h-3 w-3 text-muted-foreground" />
                <Label className="text-xs text-muted-foreground">Store Currency</Label>
              </div>
              <Select
                value={preferences.currency}
                onValueChange={(value) => {
                  setPreferences(prev => ({ ...prev, currency: value }));
                  handleSave('currency', value);
                }}
                disabled={!canEdit || saving}
              >
                <SelectTrigger className="h-9 max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">₹ INR - Indian Rupee</SelectItem>
                  <SelectItem value="USD">$ USD - US Dollar</SelectItem>
                  <SelectItem value="EUR">€ EUR - Euro</SelectItem>
                  <SelectItem value="GBP">£ GBP - British Pound</SelectItem>
                  <SelectItem value="AUD">$ AUD - Australian Dollar</SelectItem>
                  <SelectItem value="CAD">$ CAD - Canadian Dollar</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Currency used in emails and payment notifications.
              </p>
            </div>

            <div className="p-3 rounded-lg border bg-muted/30 space-y-3">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-3 w-3 text-muted-foreground" />
                <Label className="text-xs font-medium">Minimum Split Order Value</Label>
              </div>
              <div className="space-y-2">
                <Label htmlFor="minimum-split-order-value" className="text-xs text-muted-foreground">
                  Minimum amount
                </Label>
                <div className="flex items-center gap-2 max-w-xs">
                  <span className="text-sm font-medium text-muted-foreground">
                    {getCurrencySymbol(preferences.currency)}
                  </span>
                  <Input
                    id="minimum-split-order-value"
                    type="number"
                    min="0"
                    step="0.01"
                    value={preferences.minimum_split_order_value ?? 0}
                    onChange={(event) => {
                      const value = Math.max(0, Number(event.target.value) || 0);
                      setPreferences((prev) => ({ ...prev, minimum_split_order_value: value }));
                    }}
                    onBlur={(event) => {
                      const value = Math.max(0, Number(event.target.value) || 0);
                      handleSave('minimum_split_order_value', value);
                    }}
                    disabled={!canEdit || saving}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Enter 0 to allow Split with Friends for orders of any value. Customers below the minimum will see the required amount.
                </p>
              </div>
            </div>

            <div className="p-3 rounded-lg border bg-muted/30 space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="h-3 w-3 text-muted-foreground" />
                <Label className="text-xs font-medium">Customer PAN Collection</Label>
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Show PAN fields</p>
                  <p className="text-xs text-muted-foreground">
                    Turn this off to remove PAN collection for organizers and contributors.
                  </p>
                </div>
                <Switch
                  checked={preferences.customer_pan_collection_enabled !== false}
                  onCheckedChange={(checked) => {
                    setPreferences((prev) => ({
                      ...prev,
                      customer_pan_collection_enabled: checked,
                      customer_pan_required: checked ? prev.customer_pan_required : false,
                    }));
                    handleSave('customer_pan_collection_enabled', checked);
                    if (!checked && preferences.customer_pan_required) {
                      handleSave('customer_pan_required', false);
                    }
                  }}
                  disabled={!canEdit || saving}
                />
              </div>
              {preferences.customer_pan_collection_enabled !== false && (
                <div className="flex items-center justify-between gap-4 pt-3 border-t">
                  <div className="space-y-1">
                  <p className="text-sm font-medium">Require PAN from organizer and contributors</p>
                  <p className="text-xs text-muted-foreground">
                    When enabled, each customer must provide a valid PAN before starting or completing split payment.
                  </p>
                  </div>
                  <Switch
                    checked={preferences.customer_pan_required === true}
                    onCheckedChange={(checked) => {
                      setPreferences((prev) => ({ ...prev, customer_pan_required: checked }));
                      handleSave('customer_pan_required', checked);
                    }}
                    disabled={!canEdit || saving}
                  />
                </div>
              )}
              {preferences.customer_pan_collection_enabled !== false && (
                <p className="text-xs text-muted-foreground">
                  PAN remains optional when requirement is off, and customers can leave it blank.
                </p>
              )}
            </div>

            <div className="p-3 rounded-lg border bg-muted/30 space-y-3">
              <div className="flex items-center gap-2">
                <Zap className="h-3 w-3 text-muted-foreground" />
                <Label className="text-xs font-medium">Split Discount</Label>
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Enable discount for split payments</p>
                  <p className="text-xs text-muted-foreground">
                    Customers will see the discounted total before the split is calculated.
                  </p>
                </div>
                <Switch
                  checked={preferences.split_discount_enabled === true}
                  onCheckedChange={(checked) => {
                    setPreferences((prev) => ({ ...prev, split_discount_enabled: checked }));
                    handleSave('split_discount_enabled', checked);
                  }}
                  disabled={!canEdit || saving}
                />
              </div>

              {preferences.split_discount_enabled && (
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Discount Percentage</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={preferences.split_discount_percentage ?? ""}
                        onChange={(e) => setPreferences((prev) => ({
                          ...prev,
                          split_discount_percentage: e.target.value === "" ? null : parseFloat(e.target.value),
                        }))}
                        onBlur={(e) => handleSave('split_discount_percentage', e.target.value === "" ? null : parseFloat(e.target.value) || 0)}
                        className="w-24 h-9"
                        disabled={!canEdit}
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Upto Discount</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{getCurrencySymbol((preferences.currency as any) || 'USD')}</span>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        placeholder="No cap"
                        value={preferences.split_discount_cap ?? ""}
                        onChange={(e) => setPreferences((prev) => ({
                          ...prev,
                          split_discount_cap: e.target.value === "" ? null : parseFloat(e.target.value),
                        }))}
                        onBlur={(e) => handleSave('split_discount_cap', e.target.value === "" ? null : parseFloat(e.target.value) || 0)}
                        className="w-28 h-9"
                        disabled={!canEdit}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Leave blank to apply the full percentage without a cap.
                    </p>
                  </div>
                </div>
              )}
            </div>

              <div className="p-3 rounded-lg border bg-muted/30 space-y-3">
                <div className="flex items-center gap-2">
                  <Ban className="h-3 w-3 text-muted-foreground" />
                  <Label className="text-xs font-medium">Cancellation Fee</Label>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Fee Type</Label>
                  <Select
                    value={preferences.cancellation_fee_type || 'none'}
                    onValueChange={(value: 'none' | 'fixed' | 'percentage') => {
                      setPreferences(prev => ({ ...prev, cancellation_fee_type: value }));
                      handleSave('cancellation_fee_type', value);
                    }}
                    disabled={!canEdit || saving}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Cancellation Fee</SelectItem>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {preferences.cancellation_fee_type && preferences.cancellation_fee_type !== 'none' && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {preferences.cancellation_fee_type === 'percentage' ? 'Percentage' : 'Amount'}
                    </Label>
                    <div className="flex gap-2 items-center">
                      {preferences.cancellation_fee_type !== 'percentage' && <span className="text-sm text-muted-foreground">{getCurrencySymbol((preferences.currency as any) || 'USD')}</span>}
                      <Input
                        type="number"
                        min="0"
                        max={preferences.cancellation_fee_type === 'percentage' ? 100 : 10000}
                        step={preferences.cancellation_fee_type === 'percentage' ? 0.5 : 10}
                        value={preferences.cancellation_fee_value ?? 100}
                        onChange={(e) => setPreferences(prev => ({ ...prev, cancellation_fee_value: parseFloat(e.target.value) || 0 }))}
                        onBlur={(e) => handleSave('cancellation_fee_value', parseFloat(e.target.value) || 0)}
                        className="w-20 h-9"
                        disabled={!canEdit}
                      />
                      {preferences.cancellation_fee_type === 'percentage' && <span className="text-sm text-muted-foreground">%</span>}
                    </div>
                    
                    <p className="text-xs text-muted-foreground">
                      Your share: <span className="font-medium text-foreground">
                        {preferences.cancellation_fee_type === 'fixed' 
                          ? formatCurrency((preferences.cancellation_fee_value ?? 100) / 2, (preferences.currency as any) || 'USD')
                          : `${((preferences.cancellation_fee_value ?? 0) / 2).toFixed(1)}%`
                        }
                      </span> (50% split with Splitkart)
                    </p>
                  </div>
                )}
                
                <p className="text-xs text-muted-foreground">
                  {preferences.cancellation_fee_type === 'none' 
                    ? "No fee will be charged if a split payment expires incomplete."
                    : "Fee charged to the organizer if a split payment expires incomplete."
                  }
                </p>
              </div>
          </AccordionContent>
        </AccordionItem>


        {/* Payment Reminders */}
        <AccordionItem value="reminders" className="border rounded-lg px-4 bg-card">
          <AccordionTrigger className="hover:no-underline py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <BellRing className="h-4 w-4 text-primary" />
              </div>
              <div className="text-left">
                <p className="font-medium">Payment Reminders</p>
                <p className="text-xs text-muted-foreground font-normal">
                  {notificationPreferences.reminder_enabled ? 'Enabled' : 'Disabled'} • {notificationPreferences.daily_reminder_enabled ? 'Daily reminders on' : 'Daily reminders off'}
                </p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Configure automated reminders sent to participants and organizers about pending payments
            </p>
            
            {/* Master Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/50">
              <div className="flex items-center gap-2">
                <BellRing className="h-4 w-4 text-muted-foreground" />
                <div>
                  <Label className="text-sm font-medium">Enable Payment Reminders</Label>
                  <p className="text-xs text-muted-foreground">
                    Automatically remind participants who haven't paid
                  </p>
                </div>
              </div>
              <Switch
                checked={notificationPreferences.reminder_enabled}
                onCheckedChange={(checked) => 
                  setNotificationPreferences(prev => ({ ...prev, reminder_enabled: checked }))
                }
                disabled={!canEdit}
                className=""
              />
            </div>
            
            {notificationPreferences.reminder_enabled && (
              <div className="space-y-4 pt-3 border-t">
                {/* Immediate reminder - always on */}
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div className="flex-1">
                    <Label className="text-sm font-medium text-green-700 dark:text-green-400">Immediate Notification</Label>
                    <p className="text-xs text-green-600 dark:text-green-500">
                      Sent automatically when order is created
                    </p>
                  </div>
                  <Badge variant="outline" className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700">
                    Always On
                  </Badge>
                </div>
                
                {/* Daily reminder toggle */}
                <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/50">
                  <div className="flex items-center gap-3">
                    <Timer className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <Label className="text-sm font-medium">Every 24 Hours</Label>
                      <p className="text-xs text-muted-foreground">Send reminder daily until payment or deadline</p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationPreferences.daily_reminder_enabled}
                    onCheckedChange={(checked) => 
                      setNotificationPreferences(prev => ({ ...prev, daily_reminder_enabled: checked }))
                    }
                    disabled={!canEdit}
                    className=""
                  />
                </div>
                
                {/* One hour before expiry toggle */}
                <div className="flex items-center justify-between p-3 rounded-lg border bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                    <div>
                      <Label className="text-sm font-medium text-amber-700 dark:text-amber-400">1 Hour Before Expiry</Label>
                      <p className="text-xs text-amber-600 dark:text-amber-500">Urgent reminder before deadline</p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationPreferences.one_hour_reminder_enabled}
                    onCheckedChange={(checked) => 
                      setNotificationPreferences(prev => ({ ...prev, one_hour_reminder_enabled: checked }))
                    }
                    disabled={!canEdit}
                    className=""
                  />
                </div>
                
                {/* Optional additional reminder */}
                <div className="p-4 rounded-lg border border-dashed bg-muted/20 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-primary" />
                      <Label className="text-sm font-medium">Add Custom Reminder</Label>
                    </div>
                    <Switch
                      checked={notificationPreferences.additional_reminder_enabled}
                      onCheckedChange={(checked) => 
                        setNotificationPreferences(prev => ({ ...prev, additional_reminder_enabled: checked }))
                      }
                      disabled={!canEdit}
                      className=""
                    />
                  </div>
                  
                  {notificationPreferences.additional_reminder_enabled && (
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Reminder Timing</Label>
                      <Select
                        value={String(notificationPreferences.additional_reminder_hours)}
                        onValueChange={(value) => 
                          setNotificationPreferences(prev => ({ ...prev, additional_reminder_hours: Number(value) }))
                        }
                        disabled={!canEdit}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">At midpoint</SelectItem>
                          <SelectItem value="6">6 hours before deadline</SelectItem>
                          <SelectItem value="12">12 hours before deadline</SelectItem>
                          <SelectItem value="36">36 hours before deadline</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <p className="text-xs text-muted-foreground">
                    {notificationPreferences.additional_reminder_enabled 
                      ? "An additional reminder will be sent at the configured time"
                      : "Enable to add an extra reminder between your scheduled ones"}
                  </p>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Reminders are sent via email to participants who haven't completed payment
                </p>
                
                {/* Email Preview */}
                <div className="flex flex-wrap gap-2 pt-3 border-t">
                  <EmailPreviewDialog 
                    storeName={preferences.store_name || 'Your Store'} 
                    brandColor={preferences.brand_color || '#8B5CF6'} 
                  />
                </div>
              </div>
            )}

            <Button 
              size="sm" 
              onClick={saveNotificationPreferences} 
              disabled={savingNotificationPrefs || !canEdit}
              className=""
            >
              {savingNotificationPrefs ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : null}
              Save Reminder Settings
            </Button>
          </AccordionContent>
        </AccordionItem>

        {/* Team Management - Only for Super Admins */}
        {isSuperAdmin && (
          <AccordionItem value="team" className="border rounded-lg px-4 bg-card">
            <AccordionTrigger className="hover:no-underline py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Users className="h-4 w-4 text-primary" />
                </div>
                <div className="text-left">
                  <p className="font-medium">Team Management</p>
                  <p className="text-xs text-muted-foreground font-normal">
                    {teamMembers.length > 0 
                      ? `${teamMembers.length} member${teamMembers.length !== 1 ? 's' : ''} (including you)`
                      : '1 member (you)'
                    } • {invitations.length} pending invite{invitations.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-4 space-y-4">
              {/* Team Members */}
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Team Members</Label>
                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={fetchTeamData}>
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                  <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <UserPlus className="h-3 w-3 mr-2" />
                        Invite
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Invite Team Member</DialogTitle>
                        <DialogDescription>
                          Send an invitation to join your store's team
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="invite-email">Email Address</Label>
                          <Input
                            id="invite-email"
                            type="email"
                            placeholder="teammate@example.com"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="invite-role">Role</Label>
                          <Select value={inviteRole} onValueChange={setInviteRole}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a role" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="manager">
                                <div className="flex flex-col items-start">
                                  <span className="font-medium">Manager</span>
                                  <span className="text-xs text-muted-foreground">Can manage payments, refunds, and view all data</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="view_only">
                                <div className="flex flex-col items-start">
                                  <span className="font-medium">View Only</span>
                                  <span className="text-xs text-muted-foreground">Can only view data, no actions allowed</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleSendInvite} disabled={sendingInvite}>
                          {sendingInvite ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Mail className="h-4 w-4 mr-2" />
                              Send Invitation
                            </>
                          )}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              {/* Member List */}
              <div className="space-y-2">
                {/* Always show the owner (super_admin) first */}
                {teamMembers.filter(m => m.role === 'super_admin').length > 0 ? (
                  teamMembers.filter(m => m.role === 'super_admin').map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-3 rounded-lg border-2 border-primary/30 bg-primary/5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                          <Shield className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {ownerLabel} <span className="text-xs text-muted-foreground">(You)</span>
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Joined {formatDistanceToNow(new Date(member.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                      <Badge className="bg-primary text-primary-foreground">
                        Owner
                      </Badge>
                    </div>
                  ))
                ) : (
                  <div className="flex items-center justify-between p-3 rounded-lg border-2 border-primary/30 bg-primary/5">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                        <Shield className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">
                          {ownerLabel} <span className="text-xs text-muted-foreground">(You)</span>
                        </p>
                        <p className="text-xs text-muted-foreground">Primary admin</p>
                      </div>
                    </div>
                    <Badge className="bg-primary text-primary-foreground">
                      Owner
                    </Badge>
                  </div>
                )}
                
                {/* Other team members */}
                {teamMembers.filter(m => m.role !== 'super_admin').map((member) => {
                  const RoleIcon = member.role === 'manager' ? Settings2 : Eye;
                  
                  return (
                    <div key={member.id} className="flex items-center justify-between p-3 rounded-lg border bg-background">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                          <RoleIcon className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">Team Member</p>
                          <p className="text-xs text-muted-foreground">
                            Joined {formatDistanceToNow(new Date(member.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={getRoleBadgeVariant(member.role)}>
                          {getRoleLabel(member.role)}
                        </Badge>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {member.role !== 'manager' && (
                              <DropdownMenuItem onClick={() => handleUpdateMemberRole(member.id, 'manager', member.role)}>
                                <Settings2 className="h-4 w-4 mr-2" />
                                Make Manager
                              </DropdownMenuItem>
                            )}
                            {member.role !== 'view_only' && (
                              <DropdownMenuItem onClick={() => handleUpdateMemberRole(member.id, 'view_only', member.role)}>
                                <Eye className="h-4 w-4 mr-2" />
                                Make View Only
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => handleRemoveMember(member.id, member.role)}
                              disabled={removingMemberId === member.id}
                            >
                              {removingMemberId === member.id ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4 mr-2" />
                              )}
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pending Invitations */}
              {invitations.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Pending Invitations</Label>
                  {invitations.map((invitation) => (
                    <div key={invitation.id} className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Mail className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{invitation.email}</p>
                          <p className="text-xs text-muted-foreground">
                            Expires {formatDistanceToNow(new Date(invitation.expires_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{getRoleLabel(invitation.role)}</Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleRevokeInvitation(invitation.id)}
                          disabled={revokingInviteId === invitation.id}
                        >
                          {revokingInviteId === invitation.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <XCircle className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Communication & Branding */}
        <AccordionItem value="communication" className="border rounded-lg px-4 bg-card">
          <AccordionTrigger className="hover:no-underline py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Palette className="h-4 w-4 text-primary" />
              </div>
              <div className="text-left flex items-center gap-3">
                <div>
                  <p className="font-medium">Communication & Branding</p>
                  <p className="text-xs text-muted-foreground font-normal">Email reports, notifications & brand styling</p>
                </div>
                <div 
                  className="w-5 h-5 rounded-full border-2 border-background shadow-sm" 
                  style={{ backgroundColor: preferences.brand_color || '#8B5CF6' }}
                />
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-4">
            {/* Business/Store Name in Emails */}
            <div className="p-3 rounded-lg border bg-muted/30 space-y-3">
              <div className="space-y-1">
                <Label className="text-xs font-medium">{storeLabel} Name in Emails</Label>
                <p className="text-xs text-muted-foreground">
                  {`How your store name appears in customer emails (e.g., "Your order from <strong>StoreName</strong> via Splitkart")`}
                </p>
              </div>
              <Input
                type="text"
                placeholder={preferences.store_name || `Your ${storeLabel} Name`}
                value={preferences.email_store_name || ''}
                onChange={(e) => setPreferences(prev => ({ ...prev, email_store_name: e.target.value }))}
                onBlur={(e) => handleSave('email_store_name', e.target.value || null)}
                className="h-9"
                disabled={!canEdit}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to use your {storeLabel.toLowerCase()} name: <strong>{preferences.store_name || storeDomain}</strong>
              </p>
            </div>

            {/* Brand Color */}
            <div className="p-3 rounded-lg border bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Brand Color</Label>
                  <p className="text-xs text-muted-foreground">
                    Used in emails, payment pages & widget
                  </p>
                </div>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={preferences.brand_color || '#8B5CF6'}
                    onChange={(e) => setPreferences(prev => ({ ...prev, brand_color: e.target.value }))}
                    onBlur={(e) => handleSave('brand_color', e.target.value)}
                    className="w-10 h-9 rounded-lg border cursor-pointer"
                    disabled={!canEdit}
                  />
                  <Input
                    type="text"
                    value={preferences.brand_color || '#8B5CF6'}
                    onChange={(e) => setPreferences(prev => ({ ...prev, brand_color: e.target.value }))}
                    onBlur={(e) => handleSave('brand_color', e.target.value)}
                    className="w-24 h-9 font-mono text-xs"
                    disabled={!canEdit}
                  />
                </div>
              </div>
            </div>

            {/* Email Reports Settings */}
            <div className="p-3 rounded-lg border bg-muted/30 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${reportPreferences.email_reports_enabled ? "bg-green-500/10" : "bg-muted"}`}>
                    <Mail className={`h-4 w-4 ${reportPreferences.email_reports_enabled ? "text-green-500" : "text-muted-foreground"}`} />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Email Reports</Label>
                    <p className="text-xs text-muted-foreground">
                      {reportPreferences.email_reports_enabled ? scheduleDescription : "Currently disabled"}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={reportPreferences.email_reports_enabled}
                  onCheckedChange={(checked) => 
                    setReportPreferences(prev => ({ ...prev, email_reports_enabled: checked }))
                  }
                  disabled={!canEdit}
                />
              </div>

              {reportPreferences.email_reports_enabled && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Frequency
                      </Label>
                      <Select
                        value={reportPreferences.report_frequency}
                        onValueChange={(value: "weekly" | "monthly") => 
                          setReportPreferences(prev => ({ 
                            ...prev, 
                            report_frequency: value,
                            report_day: value === "weekly" ? 1 : 1
                          }))
                        }
                        disabled={!canEdit}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        {reportPreferences.report_frequency === "weekly" ? "Day of Week" : "Day of Month"}
                      </Label>
                      <Select
                        value={reportPreferences.report_day.toString()}
                        onValueChange={(value) => 
                          setReportPreferences(prev => ({ ...prev, report_day: parseInt(value) }))
                        }
                        disabled={!canEdit}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {reportPreferences.report_frequency === "weekly"
                            ? DAYS_OF_WEEK.map((day) => (
                                <SelectItem key={day.value} value={day.value.toString()}>
                                  {day.label}
                                </SelectItem>
                              ))
                            : DAYS_OF_MONTH.map((day) => (
                                <SelectItem key={day.value} value={day.value.toString()}>
                                  {day.label}
                                </SelectItem>
                              ))
                          }
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="p-2 bg-primary/5 border border-primary/20 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-primary mt-0.5" />
                      <p className="text-xs text-muted-foreground">
                        A {reportPreferences.report_frequency} report will be sent to{" "}
                        <span className="font-medium text-foreground">{merchantEmail || "store admin email"}</span>{" "}
                        {scheduleDescription.toLowerCase()}. Add team members in the Team tab to include others.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={saveReportPreferences} 
                      disabled={savingReportPrefs || !canEdit}
                      className=""
                    >
                      {savingReportPrefs ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : null}
                      Save
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={sendTestReport} 
                      disabled={sendingTestReport || !canEdit}
                      className=""
                    >
                      {sendingTestReport ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : <Send className="h-3 w-3 mr-2" />}
                      Send Test
                    </Button>
                  </div>
                </>
              )}

              {!reportPreferences.email_reports_enabled && (
                <Button 
                  size="sm" 
                  onClick={saveReportPreferences} 
                  disabled={savingReportPrefs || !canEdit}
                  className=""
                >
                  {savingReportPrefs ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : null}
                  Save
                </Button>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Alert className="bg-muted/30 border-muted">
        <Zap className="h-4 w-4" />
        <AlertDescription className="text-xs">
          Changes save automatically and apply to new split payment orders.
        </AlertDescription>
      </Alert>

    </div>
  );
};

export default MerchantPreferences;
